import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BindingBehavior} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {FieldPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {IPropertyConfig} from '@mintdata/mint-kit/api/properties/PropertyConfig';
import {EditorId, IPropertyEditorProps} from '@mintdata/mint-kit/api/properties/PropertyEditor';
import {IPropertyEditorPartProps, UserInputValue} from '@mintdata/mint-kit/api/properties/PropertyEditorPart';
import {PropertyEditorViewState} from '@mintdata/mint-kit/api/properties/PropertyEditorViewState';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {bind} from 'bind-decorator';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

const styles = require('./PropertyView.less');

type RelatedProps = {
    [relatedPropName:string]:Prop<any>
}

interface IPropertyViewProps
{
    componentDesc:ComponentDesc;
    prop:Prop<any>;
    hideTitle?:boolean;
    provideTitleAsLabel?:boolean;
    isDisabled?:boolean;
    isParentProperty?:boolean;
}

@observer
export class PropertyView extends React.Component<IPropertyViewProps, any>
{
    public constructor(props:IPropertyViewProps)
    {
        super(props);
    }

    @action
    private _selectProp(prop:Prop<any>, propPath:PropPath):boolean
    {
        controllers.ui.bindings.editPropBinding(prop, propPath);
        return true;
    }

    @action
    private _bindProp(prop:Prop<any>, propPath:PropPath):boolean
    {
        controllers.ui.bindings.startPropBinding(prop, propPath);
        return true;
    }

    @action
    private _unbindProp(prop:Prop<any>, propPath:PropPath):boolean
    {
        controllers.ui.bindings.unbindProp(prop, propPath);
        return true;
    }

    @bind
    private _onChange(propPath:PropPath):(userInput:UserInputValue) => boolean
    {
        return (userInput:UserInputValue) =>
        {
            const {componentDesc, prop} = this.props;

            if( componentDesc && propPath && prop )
                return componentDesc.changePropValue(prop, propPath, userInput);

            return false;
        };
    }

    @bind
    private _onConfirm(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {componentDesc, prop} = this.props;

            if( componentDesc && propPath && prop )
                return componentDesc.confirmPropValue(prop, propPath);

            return false;
        };
    }

    @bind
    private _onFocus(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {componentDesc, prop} = this.props;
            return true;
        };
    }

    @bind
    private _onBlur(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {componentDesc, prop} = this.props;

            if( componentDesc && propPath && prop )
                return componentDesc.confirmPropValue(prop, propPath);

            return false;
        };
    }

    @bind
    private _onBindButtonClick(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {prop} = this.props;
            return this._bindProp(prop, propPath);
        };
    }

    @bind
    private _onUnbindButtonClick(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {prop} = this.props;
            return this._unbindProp(prop, propPath);
        };
    }

    @bind
    private _onBoundPartClick(propPath:PropPath):() => boolean
    {
        return () =>
        {
            const {prop} = this.props;
            return this._selectProp(prop, propPath);
        };
    }

    @bind
    private _getEditorPartProps<FT>(propPath:PropPath):IPropertyEditorPartProps<FT>
    {
        const {prop, provideTitleAsLabel} = this.props;

        if( prop )
        {
            const partProps:IPropertyEditorPartProps<FT> = this._getBasicEditorPartProps<FT>(prop, propPath) as any;

            if( partProps )
            {
                partProps.onChange = this._onChange(propPath);
                partProps.onConfirm = this._onConfirm(propPath);
                partProps.onFocus = this._onFocus(propPath);
                partProps.onBlur = this._onBlur(propPath);
                partProps.onLensClick = () => true;
                partProps.onBindButtonClick = this._onBindButtonClick(propPath);
                partProps.onUnbindButtonClick = this._onUnbindButtonClick(propPath);
                partProps.onBoundPartClick = this._onBoundPartClick(propPath);

                if( provideTitleAsLabel )
                    partProps.label = prop.title;

                if( Type.isInstanceOf(partProps.value, Type.Error) )
                    partProps.isBindingExprError = true;

                return partProps;
            }
        }

        return null;
    }

    @bind
    private _getBasicEditorPartProps<FT>(prop:Prop<any>, propPath:PropPath):Partial<IPropertyEditorPartProps<FT>>
    {
        const {bindings} = controllers.ui;

        const componentDesc = prop.box ? prop.box.componentDesc : null;

        const converter = prop.getPropConverter<FT>(propPath);
        const propConfig = prop.getPropertyConfig(propPath);
        const {behavior, iconPlace, tooltipPlace} = _get(propConfig, 'spec.binding', {});

        const viewState = bindings.getViewState();
        if( !viewState )
            return null;

        const isSelected = viewState.currentProp === prop && viewState.currentPath.isEqual(propPath);

        const editorPartState = prop.getEditorPartState<FT>(propPath);
        if( !editorPartState )
            return null;

        /**
         * todo:
         * prop.getBinding method returns undefined for some of the system props (layoutProperties, baseProperties)
         * unitsString path causes this issue
         */

        const value = prop.getPropValue<FT>(propPath);
        const binding = prop.getBinding(propPath);
        const canBind = prop.canBindPath(propPath);
        const bindingExpr = binding && componentDesc
            ? bindings.getBindingExpression(binding, Repeater.default(componentDesc))
            : null;

        return {
            value,
            userInput: editorPartState.userInput,
            converter: converter,
            label: null,
            isValid: editorPartState.isValid,
            validationError: editorPartState.validationError,
            isFocused: editorPartState.isFocused,
            isChanged: editorPartState.isChanged,
            isConfirmed: editorPartState.isConfirmed,
            isReadonly: prop.isReadonly || !canBind,
            isSelected,
            bindingExpr,
            bindingBehavior: canBind ? behavior : BindingBehavior.NotBindable,
            bindingIconPlace: iconPlace,
            bindingTooltipPlace: tooltipPlace,
            isBound: !!binding
        } as Partial<IPropertyEditorPartProps<FT>>;
    }

    @bind
    private _getPropertyConfig<FT>(propPath:PropPath):IPropertyConfig<FT>
    {
        const {prop} = this.props;

        if( prop )
            return prop.getPropertyConfig<FT>(propPath);

        return null;
    }

    @bind
    private _getArrayItemsCount<FT>(propPath:PropPath):number
    {
        const {prop} = this.props;

        if( prop )
            return prop.getArrayItemsCount<FT>(propPath);

        return 0;
    }

    @bind
    private _addArrayItem<FT>(propPath:PropPath, index?:number, newItem?:FT):void
    {
        const {componentDesc, prop} = this.props;

        if( prop )
            prop.addArrayItem<FT>(propPath, index, newItem);

        if( componentDesc )
            componentDesc.propChanged(prop);
    }

    @bind
    private _removeArrayItem<FT>(propPath:PropPath, index?:number):void
    {
        const {componentDesc, prop} = this.props;

        if( prop )
            prop.removeArrayItem<FT>(propPath, index);

        if( componentDesc )
            componentDesc.propChanged(prop);
    }

    @bind
    private _removeArrayItems<FT>(propPath:PropPath):void
    {
        const {componentDesc, prop} = this.props;

        if( prop )
            prop.removeArrayItems<FT>(propPath);

        if( componentDesc )
            componentDesc.propChanged(prop);
    }

    @bind
    private _moveArrayItem<FT>(propPath:PropPath, from:number, to:number):void
    {
        const {componentDesc, prop} = this.props;

        if( prop )
            prop.moveArrayItem<FT>(propPath, from, to);

        if( componentDesc )
            componentDesc.propChanged(prop);
    }

    @bind
    private _setSelectedTabId(selectedTabId:PropertyPanelTabId):void
    {
        controllers.ui.surfacePropertiesPanel.setSelectedTabId(selectedTabId);
    }

    @bind
    private _getEditorViewStateSetter(editorId:EditorId):(state:PropertyEditorViewState) => void
    {
        return (state:PropertyEditorViewState) => controllers.ui.surfacePropertiesPanel.setEditorViewState(editorId, state);
    }

    @action.bound
    private _copyValuesInsideProp(fromToPathsMap:Map<FieldPath, FieldPath>):void
    {
        const {prop} = this.props;

        const clonedPropValue:any = prop.cloneValue();

        for( const [fromPath, toPath] of Array.from(fromToPathsMap.entries()) )
        {
            const fromPropPath:PropPath = PropPath.fromFieldPath(fromPath);
            const toPropPath:PropPath = PropPath.fromFieldPath(toPath);

            prop.setPropValue(toPropPath, fromPropPath.getIn(clonedPropValue));
        }
    }

    @bind
    private _areValuesEqualInsideProp(comparePathsMap:Map<FieldPath, FieldPath>):boolean
    {
        const {prop} = this.props;

        return Array.from(comparePathsMap.entries()).every(([target, compareTo]) =>
        {
            const targetPropPath:PropPath = PropPath.fromFieldPath(target);
            const compareToPropPath:PropPath = PropPath.fromFieldPath(compareTo);

            const targetValue:any = targetPropPath.getIn(prop.value);
            const compareToValue:any = compareToPropPath.getIn(prop.value);

            // do not need to compare constructors
            return _isEqual(
                JSON.parse(JSON.stringify(targetValue)),
                JSON.parse(JSON.stringify(compareToValue))
            );
        });
    }

    @bind
    private async _loadFileIntoComponent(file:File):Promise<void>
    {
        const {surfaceFileUpload, surfacePropertiesPanel} = controllers.ui;

        const {box} = this.props.componentDesc;
        const selectedTab:PropertyPanelTabId = surfacePropertiesPanel.getSelectedTabId();

        return surfaceFileUpload.loadFileIntoBox(file, box, selectedTab);
    }

    @bind
    private _getRelatedProp(propName:PropName, isParentProp:boolean):any
    {
        const {componentDesc} = this.props;

        if( isParentProp && !componentDesc.box.parent )
            return null;

        const comp:ComponentDesc = isParentProp ? componentDesc.box.parent.componentDesc : componentDesc;
        const prop:Prop<any> = comp.properties.get(propName)
            || comp.baseProperties.get(propName)
            || comp.layoutProperties.get(propName);

        return prop ? prop.value : null;
    }

    public render():JSX.Element
    {
        const {
            componentDesc,
            prop,
            hideTitle = false,
            provideTitleAsLabel = false,
            isDisabled = false,
            isParentProperty = false,
            children
        } = this.props;

        if( !prop )
        {
            console.warn('Property not defined in component', componentDesc);
            return null;
        }

        const editor = prop.editor as any;

        if( !editor )
            return null;

        const PropEditor:any = editor;

        // for now all editors use bindPath to access the api => convert it to propPath right away
        const toPropPath = (bindPath, rootPath) => PropPath.fromFieldPath(rootPath + bindPath.fieldPath);
        const editorProps:IPropertyEditorProps<any> = {
            editorId: prop.editorId,
            isBindLens: false,
            rootPath: '',
            getEditorPartProps: (b, r) => this._getEditorPartProps(toPropPath(b, r)),
            getPropertyConfig: (b, r) => this._getPropertyConfig(toPropPath(b, r)),
            getArrayItemsCount: (b, r) => this._getArrayItemsCount(toPropPath(b, r)),
            addArrayItem: (b, r, index, item) => this._addArrayItem(toPropPath(b, r), index, item),
            removeArrayItem: (b, r, index) => this._removeArrayItem(toPropPath(b, r), index),
            removeArrayItems: (b, r) => this._removeArrayItems(toPropPath(b, r)),
            moveArrayItem: (b, r, from, to) => this._moveArrayItem(toPropPath(b, r), from, to),
            setEditorViewState: this._getEditorViewStateSetter(prop.editorId),
            editorViewState: controllers.ui.surfacePropertiesPanel.getEditorViewState(prop.editorId),
            setSelectedTabId: this._setSelectedTabId,
            selectedTabId: controllers.ui.surfacePropertiesPanel.getSelectedTabId(),
            copyValuesInsideProp: this._copyValuesInsideProp,
            areValuesEqualInsideProp: this._areValuesEqualInsideProp,
            getRelatedProp: this._getRelatedProp,
            label: provideTitleAsLabel ? prop.title : null,
            isDisabled,
            isParentProperty,
            assets: {
                graphics: controllers.ui.assets.graphics.getProps(),
                fonts: controllers.ui.assets.fonts.getProps(),
                loadFileIntoComponent: this._loadFileIntoComponent
            }
        };

        (editorProps as any).beanCtor = (prop as any).beanCtor; // TODO: Need to Fix

        return (
            <div
                id={`${prop.id}_prop_view`}
                className={styles.base}
                data-prop={prop.name}
            >
                {
                    (prop.title && !hideTitle) &&
                    <div className={styles.title}>{prop.title}</div>
                }
                <div className={styles.editor}>
                    <PropEditor {...editorProps}>
                        {children}
                    </PropEditor>
                </div>
            </div>
        );
    }
}
